import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { css, Theme } from '@emotion/react';
import {
  dragAndDropFeature,
  syncDataLoaderFeature,
  type DragTarget,
  type ItemInstance,
} from '@headless-tree/core';
import { useTree } from '@headless-tree/react';
import { mapFolders, stroot } from '@birdi/js-sdk';
import PlusSVG from '@birdi/icons/plus.svg';
import { iconStyle } from '../side-bar-utils';
import { FolderButton } from './folder-button';
import { CreateFolderModal } from './create-folder-modal';
import { DeleteFolderModal } from './delete-folder-modal';
import { useMetaSelector } from '../../../meta/reducer';

const ROOT_ITEM_ID = '__map_folders_root__';

interface Folder {
  id: string;
  name: string;
}

export interface FoldersListHandle {
  sortAlphabetically: () => void;
}

interface FoldersListProps {
  onFolderCountChange?: (count: number) => void;
}

function reorderFolderIds(
  currentIds: string[],
  dragIds: string[],
  targetIndex: number,
) {
  const next = currentIds.filter((id) => !dragIds.includes(id));
  next.splice(targetIndex, 0, ...dragIds);
  return next;
}

export const FoldersList = forwardRef<FoldersListHandle, FoldersListProps>(
  function FoldersList({ onFolderCountChange }, ref) {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [errorFolders, setErrorFolders] = useState<string | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);

  const meta = useMetaSelector();
  const canCreateFolders = meta.permissions.includes('workspace:map:create');
  const canReorderFolders = canCreateFolders;

  const folderIds = useMemo(() => folders.map((folder) => folder.id), [folders]);
  const folderById = useMemo(
    () => Object.fromEntries(folders.map((folder) => [folder.id, folder])),
    [folders],
  );

  const fetchFolders = async () => {
    try {
      setLoadingFolders(true);
      const response = await mapFolders.getFolders(stroot('k1665s'));
      if (response.status === 200 && response.body) {
        setFolders(
          response.body.map((folder) => ({
            id: folder.id,
            name: folder.name,
          })),
        );
      } else {
        setErrorFolders('Failed to fetch folders');
      }
    } catch (err) {
      setErrorFolders('Failed to fetch folders');
      console.error(err);
    } finally {
      setLoadingFolders(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  const persistOrder = useCallback(
    async (orderedIds: string[]) => {
      const previous = folders;
      const reordered = orderedIds
        .map((id) => folderById[id])
        .filter((folder): folder is Folder => !!folder);
      setFolders(reordered);

      const response = await mapFolders.reorderFolders(stroot('mfre02'), {
        folderIds: orderedIds,
      });
      if (response.status !== 200) {
        setFolders(previous);
        console.error('Failed to reorder folders');
      }
    },
    [folderById, folders],
  );

  const sortAlphabetically = useCallback(() => {
    if (folders.length < 2) return;
    const sortedIds = [...folders]
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      .map((folder) => folder.id);
    void persistOrder(sortedIds);
  }, [folders, persistOrder]);

  useEffect(() => {
    onFolderCountChange?.(folders.length);
  }, [folders.length, onFolderCountChange]);

  useImperativeHandle(
    ref,
    () => ({
      sortAlphabetically,
    }),
    [sortAlphabetically],
  );

  const tree = useTree<string>({
    rootItemId: ROOT_ITEM_ID,
    getItemName: (item) => folderById[item.getId()]?.name ?? item.getId(),
    isItemFolder: () => false,
    dataLoader: {
      getItem: (itemId) => itemId,
      getChildren: (itemId) => (itemId === ROOT_ITEM_ID ? folderIds : []),
    },
    onDrop: (items: ItemInstance<string>[], target: DragTarget<string>) => {
      if (!canReorderFolders || !('childIndex' in target)) return;
      if (target.item.getId() !== ROOT_ITEM_ID) return;

      const dragIds = items.map((item) => item.getId());
      const nextOrder = reorderFolderIds(
        folderIds,
        dragIds,
        target.childIndex,
      );
      if (nextOrder.join(',') === folderIds.join(',')) return;
      void persistOrder(nextOrder);
    },
    canDrag: () => canReorderFolders,
    indent: 0,
    features: [syncDataLoaderFeature, dragAndDropFeature],
  });

  useEffect(() => {
    tree.rebuildTree();
  }, [folderIds, tree]);

  const handleDeleteFolder = (folder: Folder) => {
    setSelectedFolder(folder);
    setDeleteModalOpen(true);
  };

  const handleRenameFolder = (folder: Folder) => {
    setSelectedFolder(folder);
    setRenameModalOpen(true);
  };

  const treeItems = folderIds.length > 0 ? tree.getItems() : [];

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      {loadingFolders ? (
        <p>Loading folders...</p>
      ) : errorFolders ? (
        <p style={{ color: 'red' }}>{errorFolders}</p>
      ) : (
        <>
          <ul
            {...tree.getContainerProps()}
            css={css`
              margin: 0;
              padding: 0;
              list-style: none;
            `}
          >
            {treeItems.map((item) => {
              const folder = folderById[item.getId()];
              if (!folder) return null;

              return (
                <li
                  key={item.getId()}
                  css={css`
                    display: contents;
                  `}
                >
                  <FolderButton
                    folder={folder}
                    item={item}
                    onDelete={handleDeleteFolder}
                    onRename={handleRenameFolder}
                  />
                </li>
              );
            })}
          </ul>
          <div
            css={(theme: Theme) => css`
              height: 2px;
              background: ${theme.ocean};
              border-radius: 1px;
              pointer-events: none;
              position: absolute;
              left: 0;
              right: 0;
              z-index: 2;
            `}
            style={tree.getDragLineStyle()}
          />
        </>
      )}
      {canCreateFolders && (
        <button
          type="button"
          onClick={() => setCreateModalOpen(true)}
          css={(theme) => css`
            display: flex;
            align-items: center;
            gap: 0.5em;
            padding: 0.5em 0.75em;
            margin: 0.25em 0.5em;
            background: none;
            border: none;
            color: ${theme.midnight};
            cursor: pointer;
            border-radius: 4px;
            padding-left: 2em;
            &:hover {
              color: ${theme.deepOcean};
            }
          `}
        >
          <PlusSVG css={iconStyle} />
          <span>Create a folder</span>
        </button>
      )}
      {!loadingFolders && (
        <CreateFolderModal
          open={createModalOpen}
          onChange={setCreateModalOpen}
          onFolderCreated={fetchFolders}
          existingFolders={folders}
        />
      )}
      {!loadingFolders && selectedFolder && (
        <CreateFolderModal
          open={renameModalOpen}
          onChange={setRenameModalOpen}
          onFolderCreated={() => {
            setSelectedFolder(null);
            fetchFolders();
          }}
          selectedFolder={selectedFolder}
          existingFolders={folders}
        />
      )}
      {selectedFolder && (
        <DeleteFolderModal
          open={deleteModalOpen}
          onChange={setDeleteModalOpen}
          onFolderDeleted={() => {
            setSelectedFolder(null);
            fetchFolders();
          }}
          selectedFolder={selectedFolder}
        />
      )}
    </div>
  );
  },
);
